import '../../constants/const.css';
import { COLOR_CODE, LINE_SEQ } from '../../constants/const';
import React, { useEffect, useRef } from 'react';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ChartOptions, ChartDataset } from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

interface pitProps {
  dayCount: number[];
  minLimit: number;
  maxLimit: number;
  autoDraft: (number | null)[][];
  lineTitles: string[];
  isSelect: boolean[];
  exist?: boolean[];
}

const FONT_SIZE_14: number = 14;
const COLOR_LINE_WIDTH: number = 2;
const GRID_LINE_WIDTH: number = 0.2;
const COLOR_MIN: string = '#F4E511';
const COLOR_MAX: string = 'red';

const COLOR_ARRAY: string[] = [COLOR_CODE.PLAN, COLOR_CODE.RESULT, '#BE3E1F', COLOR_CODE.DRAFT1, COLOR_CODE.DRAFT2, COLOR_CODE.DRAFT3, COLOR_CODE.DRAFT4, COLOR_CODE.DRAFT5];
const months: number[] = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

function getMaxLimit(values: (number | null)[][]): number {
  let maxValue: number = 0;
  for (let i = 0; i < values.length; i++) {
    const row = values[i].map((val) => (val === null ? 0 : val));
    const rowMax = Math.max(...row);
    maxValue = maxValue > rowMax ? maxValue : rowMax;
  }
  return calcMathCeil(maxValue);
}

function calcMathCeil(value: number) {
  if (value >= 1) {
    const num: number = Math.ceil(value);
    const numLength: number = String(num).length;
    const val1: number = num / 10 ** (numLength - 1);
    const val2: number = Math.ceil(val1);
    if (val1 === val2) {
      return (val2 + 1) * 10 ** (numLength - 1);
    } else {
      return val2 * 10 ** (numLength - 1);
    }
  } else {
    return 1;
  }
}

const PitGraph: React.FC<pitProps> = (pitProps) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      if (ctx) {
        // 既存のチャートインスタンスが存在する場合、破棄する
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }

        const labelStr: string[] = [];
        const gridColor: string[] = [];
        const nullArray: null[] = [];

        if (pitProps.dayCount.length === 1) {
          labelStr.push('');
          nullArray.push(null);
          gridColor.push('white');
        } else {
          for (let i = 0; i < 30; i++) {
            labelStr.push('');
            nullArray.push(null);
            gridColor.push('white');
          }
        }
        if (pitProps.dayCount.length === 1) {
          for (let i = 0; i < pitProps.dayCount[0]; i++) {
            labelStr.push(String(i + 1).concat('日'));
            gridColor.push('black');
          }
        } else {
          for (let i = 0; i < pitProps.dayCount.length; i++) {
            for (let j = 0; j < pitProps.dayCount[i]; j++) {
              if (i === pitProps.dayCount.length - 1 && j === pitProps.dayCount[i] - 1) {
                labelStr.push('');
                gridColor.push('black');
              } else if (j === 0) {
                labelStr.push(String(months[i]).concat('月'));
                gridColor.push('black');
              } else {
                labelStr.push('');
                gridColor.push('white');
              }
            }
          }
        }

        const tempDatasets: ChartDataset<'line'>[] = [];
        for (let num = 0; num < pitProps.autoDraft.length; num++) {
          let isSet: boolean = true;
          if (pitProps.exist && (num === LINE_SEQ.PLAN || num === LINE_SEQ.RESULT || num === LINE_SEQ.RESULT + 1)) {
            if (!pitProps.exist[num]) {
              isSet = false;
            }
          }
          if (isSet) {
            tempDatasets.push({
              label: pitProps.lineTitles[num],
              data: [...nullArray, ...pitProps.autoDraft[num]],
              borderColor: COLOR_ARRAY[num],
              backgroundColor: COLOR_ARRAY[num],
              borderWidth: COLOR_LINE_WIDTH,
              tension: 0.1,
              pointRadius: pitProps.dayCount.length === 1 ? 5 : 0,
              hidden: !pitProps.isSelect[num],
              pointStyle: num === LINE_SEQ.PLAN ? 'rectRot' : num === LINE_SEQ.RESULT || num === LINE_SEQ.RESULT + 1 ? 'crossRot' : 'triangle',
            });
          }
        }

        if (pitProps.autoDraft.length > 0) {
          for (let num = 0; num < 2; num++) {
            const pointArray: number[] = [] as number[];
            if (pitProps.dayCount.length === 1) {
              for (let i = 0; i < pitProps.dayCount[0]; i++) {
                if (num === 0) {
                  pointArray.push(pitProps.minLimit);
                } else {
                  pointArray.push(pitProps.maxLimit);
                }
              }
            } else {
              for (let i = 0; i < pitProps.dayCount.length; i++) {
                for (let j = 0; j < pitProps.dayCount[i]; j++) {
                  if (num === 0) {
                    pointArray.push(pitProps.minLimit);
                  } else {
                    pointArray.push(pitProps.maxLimit);
                  }
                }
              }
            }
            tempDatasets.push({
              label: num === 0 ? '下限' : '上限',
              data: [...nullArray, ...pointArray],
              borderColor: num === 0 ? COLOR_MIN : COLOR_MAX,
              backgroundColor: num === 0 ? COLOR_MIN : COLOR_MAX,
              borderWidth: COLOR_LINE_WIDTH,
              tension: 0.1,
              pointRadius: 0,
            });
          }
        }

        const data = { labels: labelStr, datasets: tempDatasets };

        const options: ChartOptions<'line'> = {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: FONT_SIZE_14,
                },
                autoSkip: false,
                callback: (value: string | number, index: number) => {
                  const numericValue = typeof value === 'number' ? value : parseFloat(value);
                  const label = data.labels[numericValue];
                  return label !== '' ? label : '';
                },
                maxRotation: 0,
                minRotation: 0,
                labelOffset: pitProps.dayCount.length === 1 ? 0 : 40,
              },
              grid: {
                color: gridColor,
                drawTicks: false,
                lineWidth: GRID_LINE_WIDTH,
              },
            },
            y: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: FONT_SIZE_14,
                },
                callback: function (value) {
                  return `${value}[t]`;
                },
              },
              grid: {
                color: 'black',
                drawTicks: false,
                lineWidth: GRID_LINE_WIDTH,
              },
              max: calcMathCeil(pitProps.maxLimit) > getMaxLimit(pitProps.autoDraft) ? calcMathCeil(pitProps.maxLimit) : getMaxLimit(pitProps.autoDraft),
            },
          },
        };

        // 新しいチャートを作成して、参照を保持する
        chartInstanceRef.current = new Chart(ctx, {
          type: 'line',
          data: data,
          options: options,
        });
      }
    }

    // クリーンアップ時にチャートを破棄する
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [pitProps.dayCount, pitProps.autoDraft, pitProps.lineTitles, pitProps.isSelect]);

  return (
    <canvas
      ref={chartRef}
      style={
        pitProps.dayCount.length === 1
          ? { maxHeight: '250px', minWidth: '1154px', maxWidth: '1154px', marginLeft: '36px' }
          : { maxHeight: '250px', minWidth: '1130px', maxWidth: '1130px', marginLeft: '50px' }
      }
    />
  );
};

export default PitGraph;
